import { useTranslations } from "next-intl";
import { Gauge } from "lucide-react";
import { Skeleton } from "@chotca/ui";

export type PostingQuota = {
  open: number;
  limit: number;
  verified: boolean;
};

type QuotaStatus = "ok" | "near" | "full";

// Same color/status logic the rail card and the mobile card share, so the
// two never disagree on whether the org is "near" or "at" its limit.
// Amber kicks in earlier for the bigger (verified) tier: cap 3 warns at the
// last slot, cap 10 warns with two left — derived from the cap so it scales
// if the config tiers change.
function quotaStatus(open: number, limit: number): QuotaStatus {
  const remaining = limit - open;
  if (remaining <= 0) return "full";
  const amberThreshold = Math.max(1, Math.round(limit * 0.2));
  return remaining <= amberThreshold ? "near" : "ok";
}

const STATUS_STYLES: Record<
  QuotaStatus,
  { card: string; strip: string; bar: string; foot: string; dot: string }
> = {
  ok: {
    card: "border-smoke bg-paper",
    strip: "bg-lime",
    bar: "bg-lime",
    foot: "text-ink/60",
    dot: "bg-lime",
  },
  near: {
    card: "border-amber-200 bg-amber-50",
    strip: "bg-amber-500",
    bar: "bg-amber-500",
    foot: "text-amber-900",
    dot: "bg-amber-500",
  },
  full: {
    card: "border-red-200 bg-red-50",
    strip: "bg-red-500",
    bar: "bg-red-500",
    foot: "text-red-900 font-semibold",
    dot: "bg-red-500",
  },
};

export function PostingQuotaCard({
  quota,
  isLoading,
  className,
}: {
  quota?: PostingQuota | null;
  isLoading?: boolean;
  className?: string;
}) {
  const t = useTranslations("postings.rail");

  if (isLoading) {
    return (
      <section
        className={
          "rounded-2xl border border-smoke bg-paper p-5 " + (className ?? "")
        }
      >
        <Skeleton className="h-4 w-28" />
        <Skeleton className="mt-3 h-7 w-20" />
        <Skeleton className="mt-3 h-2 w-full" />
      </section>
    );
  }

  if (!quota) return null;

  const { open, limit, verified } = quota;
  const status = quotaStatus(open, limit);
  const remaining = Math.max(0, limit - open);
  const s = STATUS_STYLES[status];
  // Floor the fill so an empty quota still shows a sliver instead of a bare
  // track, and clamp so an over-limit count never overflows the bar.
  const percent = Math.min(100, Math.max(4, Math.round((open / limit) * 100)));

  const foot =
    status === "full"
      ? t("quotaFull")
      : status === "near"
        ? t("quotaNear", { n: remaining })
        : t("quotaRemaining", { n: remaining });

  return (
    <section
      className={
        "relative overflow-hidden rounded-2xl border p-5 " +
        s.card +
        " " +
        (className ?? "")
      }
    >
      <span
        className={"absolute inset-y-0 left-0 w-1 " + s.strip}
        aria-hidden
      />

      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-widest text-ink/60">
          <Gauge className="h-3.5 w-3.5" aria-hidden />
          <span>{t("quotaTitle")}</span>
        </div>
        <span
          className={
            "rounded-full px-2 py-0.5 text-[10px] font-bold tracking-wide " +
            (verified ? "bg-lime/15 text-lime-dark" : "bg-mist text-ash")
          }
        >
          {verified ? t("quotaTierVerified") : t("quotaTierBasic")}
        </span>
      </div>

      <div className="mt-3 flex items-baseline gap-1.5">
        <span className="text-3xl font-extrabold leading-none text-ink">
          {open}
        </span>
        <span className="text-sm font-semibold text-ink/40">
          {t("quotaUnit", { limit })}
        </span>
      </div>

      <div className="mt-3 h-2 overflow-hidden rounded-full bg-mist">
        <div
          className={"h-full rounded-full transition-all " + s.bar}
          style={{ width: `${percent}%` }}
        />
      </div>

      <p className={"mt-3 flex items-center gap-2 text-xs " + s.foot}>
        <span className={"h-1.5 w-1.5 rounded-full " + s.dot} aria-hidden />
        {foot}
        {status === "full" && !verified ? (
          <span className="text-ink/50"> · {t("quotaFullVerifyNudge")}</span>
        ) : null}
      </p>
    </section>
  );
}
